import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography, useTheme } from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { Button, CardPreOferta, PageHeader, StepActions } from "@components/ui_v2";
import { SliderCotizador } from "@components/ui_v2/cotizadoresComponents";
import { getSegmentColor } from "@components/factoraje";
import { formatCurrencyWhole } from "@helpers/funciones";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setNewConditionLoan } from "@store/slices/registerSlice";
import {
  useLazyGetConditionsByIdQuery,
  useLazyGetLoanApplicationByIdQuery,
  useLazyGetTaxSystemConfigQuery,
  usePostConditionMutation,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { useLazyGetFactoringDebtorsQuery } from "@api/factoring";
import { ApiError, FactorajeCompanyItem, LoanCondiciones, TaxSystemConfigResponse } from "@interfaces/response";
import { ALTO_SKELETON_EXPEDIENTE, STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";

/** `.card-title` / `.card-sub` del diseño. */
const CardTitulo = ({ title, sub }: { title: string; sub: string }) => (
  <>
    <Typography sx={{ fontSize: 17, fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark", mb: "4px" }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 13.5, color: "tokens.textTertiary", lineHeight: 1.55 }}>{sub}</Typography>
  </>
);

/** "plazo de 30 a 90 días"; sin plazo mínimo, "plazo hasta 90 días". */
const plazoEmpresa = ({ minTerm, maxTerm }: FactorajeCompanyItem) =>
  minTerm > 0 && minTerm !== maxTerm ? `plazo de ${minTerm} a ${maxTerm} días` : `plazo hasta ${maxTerm} días`;

/** "Tasa anual de 18% a 24%" (o una sola si coinciden); vacío si el deudor no trae tasas. */
const tasaEmpresa = ({ minRate, maxRate }: FactorajeCompanyItem) => {
  if (minRate === null && maxRate === null) return "";
  if (minRate === null || maxRate === null || minRate === maxRate) return `Tasa anual ${minRate ?? maxRate}%`;
  return `Tasa anual de ${minRate}% a ${maxRate}%`;
};

/**
 * Línea de factoraje preaprobada de Flux (`FX_PreAprobacion` del diseño): la línea revolvente con sus condiciones
 * (el usuario puede tomar una menor) y el límite por empresa.
 *
 * - Condición activa (mismo servicio que `OfertaDefault`): la línea elegida (`selected_amount`), el tope
 *   (`approved_amount`), la tasa (`interest_rate`), el aforo (`maximum_percentage_dispersion`), la comisión por
 *   operación (`extra_conditions.operation_fee`) y el plazo máximo de la factura (`extra_conditions.max_doc_term`).
 * - Solicitud: el régimen (`tax_systems.code`).
 * - Config del régimen (`getTaxSystemConfig`): el monto mínimo y el paso del slider para elegir una línea menor.
 * - Deudores de factoraje (las empresas de `UsoLinea`): solo las aprobadas (`approved`), con su límite
 *   (`approved_amount`), su plazo de factura (`min_term` a `max_term`, 0 si no vienen) y su tasa anual (`min_rate` a
 *   `max_rate`, se omite si no viene).
 *
 * Si el usuario toma una línea distinta, al aceptar se crea una condición nueva con ese monto.
 */
export const OfertaFactoraje = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(STATUS_CATALOG.oferta.code);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getDebtors] = useLazyGetFactoringDebtorsQuery();
  const [postCondition] = usePostConditionMutation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [condition, setCondition] = useState<LoanCondiciones | null>(null);
  const [taxSystemConfig, setTaxSystemConfig] = useState<TaxSystemConfigResponse | null>(null);
  const [limitesPorEmpresa, setLimitesPorEmpresa] = useState<FactorajeCompanyItem[]>([]);
  // Línea que va a tomar: la seleccionada o una menor que la aprobada, nunca mayor.
  const [linea, setLinea] = useState(0);
  const [editando, setEditando] = useState(false);

  useEffect(() => {
    const fetchOferta = async () => {
      try {
        const [conditions, loanApplication, debtors] = await Promise.all([
          getConditions(application.id).unwrap(),
          getLoanApplication(application.id).unwrap(),
          getDebtors({ loanApplicationId: application.id }).unwrap(),
        ]);
        setLimitesPorEmpresa(
          debtors
            .filter((debtor) => debtor.approved === true)
            .map((debtor) => ({
              name: debtor.company_name,
              maxAmount: debtor.approved_amount ?? 0,
              minTerm: debtor.min_term ?? 0,
              maxTerm: debtor.max_term ?? 0,
              minRate: debtor.min_rate ?? null,
              maxRate: debtor.max_rate ?? null,
            })),
        );
        const active = conditions.find((item) => item.active) ?? null;
        setCondition(active);
        if (active) {
          const seleccionada = active.selected_amount || active.loan_amount;
          setLinea(Math.min(seleccionada, active.approved_amount || seleccionada));
        }
        const taxSystemCode = loanApplication.tax_systems?.code;
        if (taxSystemCode) {
          setTaxSystemConfig(await getTaxSystemConfig(taxSystemCode).unwrap());
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "OfertaFactoraje-initial_fetch" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    };
    fetchOferta();
  }, [application.id, getConditions, getLoanApplication, getTaxSystemConfig, getDebtors, dispatch]);

  const lineaPreaprobada = condition?.approved_amount ?? 0;
  const tasa = condition?.interest_rate ?? 0;
  const plazo = condition?.extra_conditions?.max_doc_term ?? 0;
  // Sin config del régimen, el slider va de la línea aprobada a sí misma (no se puede elegir una menor).
  const lineaMin = Math.min(taxSystemConfig?.minAmount || lineaPreaprobada, lineaPreaprobada);
  const lineaStep = taxSystemConfig?.stepAmount || 1;
  const aforo = condition?.maximum_percentage_dispersion ?? 0;
  const comision = condition?.extra_conditions?.operation_fee ?? 0;

  // "Aceptar y continuar al expediente": si cambió la línea, condición nueva con ese monto; luego avanza al siguiente
  // status de `status_config` y navega a su pantalla.
  const handleAccept = async () => {
    try {
      setIsSubmitting(true);
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }
      if (condition && linea !== (condition.selected_amount || condition.loan_amount)) {
        await postCondition({
          loan_application: application.id,
          loan_amount: linea,
          requested_amount: linea,
          selected_amount: linea,
          requested_term: condition.selected_term,
          selected_term: condition.selected_term,
          user_name: `TOFU - ${accountUser.email}`,
          user_id: String(accountUser.id),
        }).unwrap();
        dispatch(
          setNewConditionLoan({
            loanAmount: linea,
            loanTerm: condition.selected_term,
            amountMonthly: condition.monthly_payment,
            approved_amount: condition.approved_amount,
            approved_term: condition.approved_term,
            interest_rate: condition.interest_rate,
          }),
        );
      }
      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "OfertaFactoraje-accept" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Análisis · Línea preaprobada"
        title="Tu línea de factoraje está preaprobada"
        subtitle={
          isLoading ? undefined : (
            <>
              Tienes una línea revolvente preautorizada de <strong>{formatCurrencyWhole(lineaPreaprobada)} MXN</strong>.
              Cada factura que descuentes libera saldo conforme te pagan tus clientes.
            </>
          )
        }
      />

      {isLoading ? (
        <Skeleton variant="rectangular" height={ALTO_SKELETON_EXPEDIENTE} sx={{ borderRadius: 2 }} />
      ) : (
        <>
          <CardPreOferta
            eyebrow="Línea revolvente · Factoraje electrónico"
            badge="Preaprobada"
            amount={formatCurrencyWhole(linea)}
            amountCaption="MXN disponibles"
            amountAction={
              <Button
                tone="compact"
                startIcon={editando ? undefined : <EditOutlined sx={{ fontSize: 12 }} />}
                onClick={() => setEditando((value) => !value)}
                data-testid="oferta-factoraje-editar-linea-button"
              >
                {editando ? "Listo" : "Seleccionar menor monto de línea"}
              </Button>
            }
            sub="Vigente por 30 días · Sujeta a revisión documental y validación de cada factura"
            items={[
              { label: "Aforo", value: `${aforo}%` },
              { label: "Tasa anual", value: `${tasa}%` },
              { label: "Comisión por operación", value: `${comision}%` },
              { label: "Plazo máximo de factura", value: `${plazo} días` },
            ]}
          >
            {editando && (
              // `.fxu-edit-row`: separado de la cifra por la línea de la marca (Flux: #E5E5DA).
              <Box sx={{ mt: 2, pt: "14px", borderTop: "1px solid", borderColor: "divider" }}>
                <SliderCotizador
                  value={linea}
                  onChange={(value) => setLinea(Math.min(value, lineaPreaprobada))}
                  min={lineaMin}
                  max={lineaPreaprobada}
                  step={lineaStep}
                  minLabel={formatCurrencyWhole(lineaMin)}
                  maxLabel={`Preautorizado ${formatCurrencyWhole(lineaPreaprobada)}`}
                  loading={false}
                  testId="oferta-factoraje-linea-slider"
                />
                <Typography variant="body3" color="tokens.textTertiary" component="p" sx={{ mt: 0.75 }}>
                  Puedes tomar menos de tu línea preautorizada; no puedes subirla por encima de{" "}
                  {formatCurrencyWhole(lineaPreaprobada)}.
                </Typography>
              </Box>
            )}
          </CardPreOferta>

          <Paper variant="lendia_paper" sx={{ mt: 2 }}>
            <CardTitulo
              title="Límites por empresa"
              sub="Cada empresa tiene un monto máximo y un plazo máximo de factura autorizados según su riesgo. Puedes operar con cada una hasta su límite, siempre dentro de tu línea."
            />
            {limitesPorEmpresa.length === 0 ? (
              <Typography
                variant="body2"
                color="tokens.textTertiary"
                component="p"
                sx={{ mt: "12px" }}
                data-testid="oferta-factoraje-sin-empresas-autorizadas"
              >
                Aún no hay empresas autorizadas. Te avisaremos en cuanto tengan su límite asignado.
              </Typography>
            ) : (
              // `.fxu-rangos`: chip del color de la empresa (el mismo que en el reparto de la línea), nombre y límite.
              <Box
                sx={{
                  mt: "12px",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                  gap: "8px 20px",
                }}
              >
                {limitesPorEmpresa.map((empresa, i) => (
                  <Box key={empresa.name} sx={{ display: "grid", gridTemplateColumns: "4px 1fr", gap: "10px" }}>
                    <Box sx={{ borderRadius: "3px", minHeight: 26, bgcolor: getSegmentColor(theme, i) }} />
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: "text.primary",
                          lineHeight: 1.3,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {empresa.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11.5,
                          color: "tokens.textTertiary",
                          fontVariantNumeric: "tabular-nums",
                          mt: "1px",
                        }}
                      >
                        Máximo {formatCurrencyWhole(empresa.maxAmount)} · {plazoEmpresa(empresa)}
                      </Typography>
                      {tasaEmpresa(empresa) && (
                        <Typography
                          sx={{ fontSize: 11.5, color: "tokens.textTertiary", fontVariantNumeric: "tabular-nums" }}
                        >
                          {tasaEmpresa(empresa)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          <StepActions
            onNext={handleAccept}
            nextLabel="Aceptar y completar expediente"
            nextDisabled={isSubmitting}
            nextTestId="oferta-factoraje-aceptar-button"
          />
        </>
      )}
    </Box>
  );
};
